import { useState, useEffect } from 'react'
import { Mail, Phone, MessageSquare, Clock, CheckCircle, AlertCircle, X } from 'lucide-react'
import { db } from '@obis/firebase-config'
import { collection, query, orderBy, onSnapshot, updateDoc, doc, arrayUnion, Timestamp } from 'firebase/firestore'

interface ContactMessage {
  id: string
  contactId: string
  name: string
  email: string
  phone: string
  subject: string
  message: string
  status: 'new' | 'read' | 'responded' | 'closed'
  priority: string
  createdAt: any
  updatedAt: any
  notes: Array<{ text: string; addedAt: any; addedBy: string }>
  responses: Array<{ responseId: string; message: string; respondedBy: string; respondedAt: any }>
}

export default function ContactMessages() {
  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [responseText, setResponseText] = useState('')
  const [noteText, setNoteText] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const q = query(
      collection(db, 'contact_messages'),
      orderBy('createdAt', 'desc')
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ContactMessage[]
      setMessages(data)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const filteredMessages = statusFilter === 'all' 
    ? messages 
    : messages.filter(m => m.status === statusFilter)

  const handleStatusChange = async (messageId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'contact_messages', messageId), {
        status: newStatus,
        updatedAt: Timestamp.now()
      })
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, status: newStatus as any })
      }
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const handleAddNote = async () => {
    if (!selectedMessage || !noteText.trim()) return

    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        notes: arrayUnion({
          text: noteText,
          addedAt: Timestamp.now(),
          addedBy: 'admin'
        }),
        updatedAt: Timestamp.now()
      })
      setNoteText('')
      setSelectedMessage({
        ...selectedMessage,
        notes: [...selectedMessage.notes, {
          text: noteText,
          addedAt: Timestamp.now(),
          addedBy: 'admin'
        }]
      })
    } catch (error) {
      console.error('Error adding note:', error)
    }
  }

  const handleAddResponse = async () => {
    if (!selectedMessage || !responseText.trim()) return

    try {
      await updateDoc(doc(db, 'contact_messages', selectedMessage.id), {
        responses: arrayUnion({
          responseId: `resp_${Date.now()}`,
          message: responseText,
          respondedBy: 'admin',
          respondedAt: Timestamp.now()
        }),
        status: 'responded',
        updatedAt: Timestamp.now()
      })
      setResponseText('')
      setSelectedMessage({
        ...selectedMessage,
        status: 'responded',
        responses: [...selectedMessage.responses, {
          responseId: `resp_${Date.now()}`,
          message: responseText,
          respondedBy: 'admin',
          respondedAt: Timestamp.now()
        }]
      })
    } catch (error) {
      console.error('Error adding response:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-red-100 text-red-800'
      case 'read': return 'bg-yellow-100 text-yellow-800'
      case 'responded': return 'bg-blue-100 text-blue-800'
      case 'closed': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new': return <AlertCircle className="w-4 h-4" />
      case 'read': return <Mail className="w-4 h-4" />
      case 'responded': return <CheckCircle className="w-4 h-4" />
      case 'closed': return <CheckCircle className="w-4 h-4" />
      default: return null
    }
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return ''
    const date = timestamp.toDate?.() || new Date(timestamp)
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading contact messages...</div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      {/* Messages List */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Messages</h2>
          <div className="flex gap-2 flex-wrap">
            {['all', 'new', 'read', 'responded', 'closed'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No messages found
            </div>
          ) : (
            filteredMessages.map(message => (
              <button
                key={message.id}
                onClick={() => setSelectedMessage(message)}
                className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition ${
                  selectedMessage?.id === message.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{message.name}</p>
                    <p className="text-sm text-gray-600 truncate">{message.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1 ${getStatusColor(message.status)}`}>
                    {getStatusIcon(message.status)}
                    {message.status}
                  </span>
                </div>
                <p className="text-sm text-gray-700 truncate mb-1">{message.subject}</p>
                <p className="text-xs text-gray-500">{formatDate(message.createdAt)}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Message Details */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
        {selectedMessage ? (
          <>
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedMessage.subject}</h3>
                  <p className="text-gray-600 mt-1">From: {selectedMessage.name}</p>
                </div>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 hover:underline">
                    {selectedMessage.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-gray-900">{selectedMessage.phone || 'Not provided'}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <select
                    value={selectedMessage.status}
                    onChange={(e) => handleStatusChange(selectedMessage.id, e.target.value)}
                    className={`mt-1 px-3 py-1 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusColor(selectedMessage.status)}`}
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="responded">Responded</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Received</p>
                  <p className="text-gray-900 text-sm">{formatDate(selectedMessage.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Original Message */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 mb-2">Message</p>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {/* Responses */}
              {selectedMessage.responses.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-3">Responses</p>
                  <div className="space-y-3">
                    {selectedMessage.responses.map(response => (
                      <div key={response.responseId} className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-semibold">Admin</span> • {formatDate(response.respondedAt)}
                        </p>
                        <p className="text-gray-700 whitespace-pre-wrap">{response.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedMessage.notes.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-3">Internal Notes</p>
                  <div className="space-y-2">
                    {selectedMessage.notes.map((note, idx) => (
                      <div key={idx} className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                        <p className="text-xs text-gray-600 mb-1">{formatDate(note.addedAt)}</p>
                        <p className="text-sm text-gray-700">{note.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-200 p-6 space-y-4">
              {/* Add Note */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Add Internal Note</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddNote}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Add Response */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Send Response</label>
                <div className="flex flex-col gap-2">
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response..."
                    rows={3}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddResponse}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    Send Response
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>Select a message to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
